<template>
  <div>
    <v-card flat>
      <v-data-table
        :headers="headers"
        :items="filteredTemplates"
        :items-per-page="10"
        class="elevation-0 custom-data-table"
        :no-data-text="noDataText"
        :height="tableHeight"
        fixed-header
        :loading="tableLoading"
        density="compact"
      >
        <template v-slot:top>
          <div class="d-flex justify-space-between align-center pa-4">
            <div>
              <span class="text-h5">Templates</span>
              <!-- <div class="text-caption text-medium-emphasis mt-1">
                Manage all templates
              </div> -->
            </div>
            <div class="d-flex align-center">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                variant="outlined"
                density="compact"
                style="width: 350px; margin-right: 16px"
              ></v-text-field>
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                @click="openAddDialog"
              >
                Add Template
              </v-btn>
            </div>
          </div>
        </template>

        <template v-slot:[`item.category_name`]="{ item }">
          {{ formatNA(item.category_name) }}
        </template>

        <template v-slot:[`item.routing_type`]="{ item }">
          {{ formatNA(item.routing_type) }}
        </template>

        <template v-slot:[`item.template_description`]="{ item }">
          <v-tooltip
            v-if="
              item.template_description &&
              getPlainTextLength(item.template_description) > 50
            "
            location="top"
          >
            <template v-slot:activator="{ props }">
              <div
                v-bind="props"
                class="text-truncate"
                style="max-width: 300px"
                v-html="getTruncatedHtml(item.template_description, 50)"
              ></div>
            </template>
            <span
              style="
                max-width: 400px;
                display: inline-block;
                word-wrap: break-word;
              "
              v-html="formatNA(item.template_description)"
            >
            </span>
          </v-tooltip>
          <div
            v-else
            class="text-truncate"
            style="max-width: 300px"
            v-html="formatNA(item.template_description)"
          ></div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            icon
            size="small"
            variant="text"
            color="primary"
            @click="editTemplate(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            icon
            size="small"
            variant="text"
            color="error"
            @click="deleteTemplate(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Template Dialog -->
    <AddTemplateDialog
      v-model="showAddDialog"
      @added="handleTemplateAdded"
    />

    <!-- Edit Template Dialog -->
    <EditTemplateDialog
      v-model="showEditDialog"
      :template="selectedTemplate"
      @updated="handleTemplateUpdated"
    />

    <!-- Delete Template Dialog -->
    <DeleteTemplateDialog
      v-model="showDeleteDialog"
      :template="selectedTemplate"
      @deleted="handleTemplateDeleted"
    />
  </div>
</template>

<script>
import AddTemplateDialog from "../../components/Templates/Dailogs/AddTemplateDialog.vue";
import EditTemplateDialog from "../../components/Templates/Dailogs/EditTemplateDialog.vue";
import DeleteTemplateDialog from "../../components/Templates/Dailogs/DeleteTemplateDialog.vue";
import GetTemplates from "@/mixins/Templates/GetTemplates";

export default {
  name: "TemplatesView",
  mixins: [GetTemplates],
  components: {
    AddTemplateDialog,
    EditTemplateDialog,
    DeleteTemplateDialog,
  },
  data() {
    return {
      showAddDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      tableLoading: false,
      selectedTemplate: null,
      selectedIndex: -1,
      templates: [],
      search: "",
      windowHeight: window.innerHeight,
      headers: [
        { title: "Template Name", key: "template_name", sortable: true },
        { title: "Category", key: "category_name", sortable: true },
        { title: "Routing", key: "routing_type", sortable: true },
        {
          title: "Template Description",
          key: "template_description",
          sortable: true,
        },
        { title: "Actions", key: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    noDataText() {
      return "No templates found. Click 'Add Template' to add one.";
    },
    tableHeight() {
     return this.windowHeight - 250;
    },
    filteredTemplates() {
      if (!this.search) {
        return this.templates;
      }

      const searchTerm = this.search.toLowerCase().trim();
      return this.templates.filter((template) => {
        return (
          (template.template_name &&
            template.template_name.toLowerCase().includes(searchTerm)) ||
          (template.category_name &&
            template.category_name.toLowerCase().includes(searchTerm)) ||
          (template.routing_type &&
            template.routing_type.toLowerCase().includes(searchTerm)) ||
          (template.template_description &&
            template.template_description.toLowerCase().includes(searchTerm))
        );
      });
    },
  },
  async mounted() {
    this.updateWindowHeight();
    window.addEventListener("resize", this.updateWindowHeight);
    this.tableLoading = true;
    try {
      await this.fetchTemplates();
      this.templates = this.templatesList;
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      this.tableLoading = false;
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateWindowHeight);
  },
  methods: {
    formatNA(value) {
      if (!value || value === "N/A" || value === "n/a") return "-";
      return value;
    },
    stripHtmlTags(html) {
      if (!html) return "-";
      if (html === "N/A" || html === "n/a") return "-";

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || html;
    },
    getPlainTextLength(html) {
      if (!html) return 0;
      const plainText = this.stripHtmlTags(html);
      return plainText.length;
    },
    getTruncatedHtml(html, maxLength = 50) {
      if (!html) return "-";
      if (html === "N/A" || html === "n/a") return "-";

      const plainText = this.stripHtmlTags(html);
      if (plainText.length <= maxLength) {
        return html;
      }

      let charCount = 0;
      let truncatedHtml = "";
      let inTag = false;

      for (let i = 0; i < html.length && charCount < maxLength; i++) {
        const char = html[i];
        if (char === "<") {
          inTag = true;
          truncatedHtml += char;
        } else if (char === ">") {
          inTag = false;
          truncatedHtml += char;
        } else if (!inTag) {
          truncatedHtml += char;
          charCount++;
        } else {
          truncatedHtml += char;
        }
      }

      return truncatedHtml + "...";
    },
    updateWindowHeight() {
      this.windowHeight = window.innerHeight;
    },
    openAddDialog() {
      this.showAddDialog = true;
    },
    async handleTemplateAdded() {
      // Refetch the template list from API
      this.tableLoading = true;
      try {
        await this.fetchTemplates();
        this.templates = this.templatesList;
      } catch (error) {
        console.error("Error loading templates:", error);
      } finally {
        this.tableLoading = false;
      }
    },
    editTemplate(template) {
      this.selectedTemplate = { ...template };
      // Find the actual index in the original templates array
      this.selectedIndex = this.templates.findIndex(
        (t) => t.template_name === template.template_name
      );
      this.showEditDialog = true;
    },
    async handleTemplateUpdated() {
      // Refetch the template list from API
      this.tableLoading = true;
      try {
        await this.fetchTemplates();
        this.templates = this.templatesList;
      } catch (error) {
        console.error("Error loading templates:", error);
      } finally {
        this.tableLoading = false;
      }
      this.selectedIndex = -1;
      this.selectedTemplate = null;
    },
    deleteTemplate(template) {
      this.selectedTemplate = { ...template };
      // Find the actual index in the original templates array
      this.selectedIndex = this.templates.findIndex(
        (t) => t.template_name === template.template_name
      );
      this.showDeleteDialog = true;
    },
    async handleTemplateDeleted() {
      // Refetch the template list from API
      this.tableLoading = true;
      try {
        await this.fetchTemplates();
        this.templates = this.templatesList;
      } catch (error) {
        console.error("Error loading templates:", error);
      } finally {
        this.tableLoading = false;
      }
      this.selectedIndex = -1;
      this.selectedTemplate = null;
    },
  },
};
</script>

